import { cn } from '@liza/ui/utils';
import type { ServiceRequest } from '@medplum/fhirtypes';
import { CheckIcon, ClipboardCheckIcon, FlaskConicalIcon, SendIcon, XIcon } from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../utils/date';
import { Badge } from '../ui/badge';

export type LabOrderProgressProps = {
  serviceRequest: ServiceRequest;
};

type StepState = 'complete' | 'current' | 'upcoming' | 'rejected';
type StepKey = 'sent' | 'acknowledged' | 'testing' | 'final';

type StepIcon = React.ComponentType<{ className?: string }>;

/**
 * Derives each step's state from the order's FHIR status. Only called when the order has no
 * `DiagnosticReport` yet, so it never needs to model the resulted ("Final") state as reached.
 *
 * - `draft` — created in Medplum but not yet transmitted to the lab.
 * - `active` — accepted by the lab (the send bot flips `draft → active` on acceptance), so the
 *   sample is being processed.
 * - `completed` — terminal without a linked report (rare); results are imminent.
 * - `on-hold` / `revoked` / `entered-in-error` — the lab could not accept the order.
 */
const getStepStates = (status: ServiceRequest['status']): Record<StepKey, StepState> => {
  switch (status) {
    case 'active':
      return { sent: 'complete', acknowledged: 'complete', testing: 'current', final: 'upcoming' };
    case 'completed':
      return { sent: 'complete', acknowledged: 'complete', testing: 'complete', final: 'current' };
    case 'on-hold':
    case 'revoked':
    case 'entered-in-error':
      return { sent: 'complete', acknowledged: 'rejected', testing: 'upcoming', final: 'upcoming' };
    default:
      // draft / unknown — awaiting transmission to the lab
      return { sent: 'current', acknowledged: 'upcoming', testing: 'upcoming', final: 'upcoming' };
  }
};

const CIRCLE_CLASSES: Record<StepState, string> = {
  complete: 'bg-green-600 text-white dark:bg-green-500',
  current: 'bg-blue-600 text-white dark:bg-blue-500',
  upcoming: 'bg-muted text-muted-foreground',
  rejected: 'bg-destructive text-white',
};

/**
 * Vertical stepper showing a lab order's lifecycle (Order Sent → Order Acknowledged → Testing →
 * Final) for orders that haven't been resulted yet. Stands in for the results table in the
 * "Results" section until a `DiagnosticReport` is linked.
 */
export const LabOrderProgress = ({ serviceRequest }: LabOrderProgressProps): React.ReactElement => {
  const { t } = useTranslation('records');
  const headingId = useId();
  const states = getStepStates(serviceRequest.status);

  const orderedDate = serviceRequest.authoredOn ? formatDate(serviceRequest.authoredOn) : undefined;
  const acknowledgedBadge =
    states.acknowledged === 'rejected'
      ? t('progressRejectedBadge')
      : states.acknowledged === 'complete'
        ? t('progressAcknowledgedBadge')
        : undefined;

  const steps: { key: StepKey; icon: StepIcon; title: string; description: string; badge?: string }[] = [
    {
      key: 'sent',
      icon: SendIcon,
      title: t('progressOrderSentTitle'),
      description: t('progressOrderSentDescription'),
      badge: orderedDate,
    },
    {
      key: 'acknowledged',
      icon: states.acknowledged === 'rejected' ? XIcon : CheckIcon,
      title: t('progressOrderAcknowledgedTitle'),
      description:
        states.acknowledged === 'rejected'
          ? t('progressRejectedDescription')
          : t('progressOrderAcknowledgedDescription'),
      badge: acknowledgedBadge,
    },
    {
      key: 'testing',
      icon: FlaskConicalIcon,
      title: t('progressTestingTitle'),
      description: t('progressTestingDescription'),
    },
    {
      key: 'final',
      icon: ClipboardCheckIcon,
      title: t('progressFinalTitle'),
      description: t('progressFinalDescription'),
    },
  ];

  return (
    <section aria-labelledby={headingId} className="space-y-4">
      <h2 id={headingId} className="text-lg font-semibold">
        {t('diagnosticReport')}
      </h2>
      <ol>
        {steps.map((step, idx) => {
          const state = states[step.key];
          const isLast = idx === steps.length - 1;
          const Icon = step.icon;
          // The connector below a step is solid green only once that step is reached; otherwise
          // it's a dotted line into the not-yet-reached steps.
          const connectorReached = state === 'complete';
          return (
            <li key={step.key} className="flex gap-4">
              <div className="flex flex-col items-center">
                <span
                  className={cn(
                    'flex size-10 shrink-0 items-center justify-center rounded-full',
                    CIRCLE_CLASSES[state],
                  )}
                >
                  <Icon className="size-5" />
                </span>
                {!isLast && (
                  <span
                    className={cn(
                      'min-h-6 flex-1',
                      connectorReached
                        ? 'w-0.5 bg-green-600 dark:bg-green-500'
                        : 'w-0 border-l-2 border-dotted border-muted-foreground/40',
                    )}
                  />
                )}
              </div>
              <div className={cn('pt-1.5', isLast ? 'pb-0' : 'pb-6')}>
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className={cn('font-medium', state === 'upcoming' && 'text-muted-foreground')}>{step.title}</h3>
                  {step.badge && (
                    <Badge variant="secondary" className={step.key === 'acknowledged' ? 'uppercase' : undefined}>
                      {step.badge}
                    </Badge>
                  )}
                </div>
                <p className="mt-0.5 text-sm text-muted-foreground">{step.description}</p>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
};
